import React from 'react';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { ShieldCheck, ShieldAlert, Shield, HelpCircle } from 'lucide-react';

/**
 * Sessão 32 — QW4: Confidence badge for procurement suggestions.
 *
 * **Sessão 33 (Phase α) — silent by default**:
 * High/Medium confidence is implicit (no badge shown). Only Low and
 * Experimental tiers render — they're the ones that demand operator
 * attention. This avoids "badge soup" and signal inflation:
 * if the operator sees a confidence badge, it MEANS something.
 *
 * Set prop `forceShow` to true to always render (e.g. on detail pages,
 * or when the operator explicitly filtered to "show all signals").
 *
 * Per AI_ROLE_CHARTER.md §5: confidence must be visible AND honest.
 * "If everything is Medium, Medium means nothing."
 *
 * Props:
 *   confidence: { score: 0-100, tier: 'high'|'medium'|'low'|'experimental',
 *                 tier_label: string, reasons: string[] }
 *   size: 'sm' (default) | 'xs'
 *   forceShow: bool — override silent-by-default behavior
 *   testid: optional test id suffix
 */
export const ConfidenceBadge = ({ confidence, size = 'sm', forceShow = false, testid }) => {
  if (!confidence) return null;

  const { tier, tier_label: tierLabel, score, reasons = [] } = confidence;

  // Silent-by-default: high/medium produce no visual noise.
  // Only attention-worthy tiers render.
  if (!forceShow && (tier === 'high' || tier === 'medium')) {
    return null;
  }

  const config = {
    high: {
      icon: ShieldCheck,
      className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      label: tierLabel || 'Alta',
    },
    medium: {
      icon: Shield,
      className: 'bg-slate-50 text-slate-600 border-slate-200',
      label: tierLabel || 'Média',
    },
    low: {
      icon: ShieldAlert,
      className: 'bg-amber-50 text-amber-700 border-amber-200',
      label: tierLabel || 'Pouca informação',
    },
    experimental: {
      icon: HelpCircle,
      className: 'bg-rose-50 text-rose-700 border-rose-200',
      label: tierLabel || 'Sinal experimental',
    },
  };

  const c = config[tier] || config.experimental;
  const Icon = c.icon;
  const isXs = size === 'xs';

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            className={`${c.className} ${isXs ? 'h-5 px-1.5 text-[10px]' : 'h-6 px-2 text-xs'} font-normal cursor-help`}
            data-testid={testid ? `confidence-badge-${testid}` : 'confidence-badge'}
          >
            <Icon className={`${isXs ? 'h-2.5 w-2.5' : 'h-3 w-3'} mr-1`} />
            {c.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs bg-white border border-slate-200 shadow-lg">
          <div className="space-y-1.5">
            <p className="font-semibold text-sm text-slate-800">
              Confiança: {c.label} ({score}/100)
            </p>
            <p className="text-xs text-slate-600">
              {tier === 'high' && 'Recomendação consolidada — múltiplos sinais convergentes.'}
              {tier === 'medium' && 'Recomendação com confiança moderada.'}
              {tier === 'low' && 'Sinal preliminar — validar antes de aceitar.'}
              {tier === 'experimental' && 'Insuficiente histórico — sugestão experimental.'}
            </p>
            {reasons.length > 0 && (
              <ul className="text-xs text-slate-600 space-y-0.5 pt-1">
                {reasons.map((r, i) => (
                  <li key={`${r}-${i}`}>• {r}</li>
                ))}
              </ul>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default ConfidenceBadge;
